import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { AuthenticationResultDto, AuthService as AuthApiService, Configuration } from '@artemis-software/wr-api';
import { filter, firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { AuthCredentials } from '@models/auth-credentials';
import { NbToastrService } from '@nebular/theme';
import { toObservable } from '@angular/core/rxjs-interop';
import { ContractorCredentialsService } from '@services/contractor-credentials.service';
import { isQrApiUrl, isQrRoutePath } from '@/utils/qr-route';

export const SESSION_EXPIRED_MESSAGE = 'Die Sitzung ist abgelaufen - bitte erneut anmelden.';

export const LOGOUT_PATH = '/api/auth/logout';

export const JWT_STORAGE_KEY = 'wr-jwt';

export interface LogoutOptions {
  /**
   * Der Widerruf beendet die Sitzung des Benutzers auf ALLEN Geräten (der Stempel hängt am
   * Benutzer, nicht am Token). Nur echte Abmeldungen dürfen das auslösen. Die Interceptoren
   * melden auf 401/403 ebenfalls ab - ein 403 ist hier aber eine reguläre Antwort der
   * Rechteprüfung und darf einem Außendienstler nicht die parallele App-Sitzung abräumen.
   */
  revokeServerSession?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  readonly authCredentials = signal<AuthCredentials | undefined>(undefined);
  readonly authCredentials$ = toObservable(this.authCredentials);
  readonly loggedIn = computed(() => this.authCredentials() !== undefined);
  private readonly qrRoute = signal(isQrRoutePath(window.location.pathname));
  readonly qrRouteActive = this.qrRoute.asReadonly();
  readonly dashboardSessionActive = computed(() => this.loggedIn() && !this.qrRoute());
  readonly loggedIn$ = toObservable(this.dashboardSessionActive).pipe(filter(value => value));
  readonly loggedOut$ = toObservable(this.loggedIn).pipe(filter(value => !value));

  private readonly apiConfig = inject(Configuration);
  private readonly http = inject(HttpClient);
  private readonly authApi = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly nbToastrService = inject(NbToastrService);
  private readonly contractorCredentials = inject(ContractorCredentialsService);

  constructor() {
    this.initJwt();
    this.trackQrRoute();
  }

  public isQrSessionRequest(url: string): boolean {
    return this.qrRoute() || (isQrApiUrl(url) && this.contractorCredentials.getCurrentPin() !== null);
  }

  private trackQrRoute(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.qrRoute.set(isQrRoutePath(event.url));
      } else if (event instanceof NavigationEnd) {
        this.qrRoute.set(isQrRoutePath(event.urlAfterRedirects));
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.qrRoute.set(isQrRoutePath(this.router.url));
      }
    });
  }

  public initJwt(): void {
    this.authCredentials.set(this.readStoredCredentials());
  }

  // Ein korrupter Eintrag würde sonst im Service-Konstruktor werfen (app-weiter DI-Fehler,
  // weißer Screen) und die Login-Seite ohne manuelles Storage-Löschen unerreichbar machen.
  private readStoredCredentials(): AuthCredentials | undefined {
    const jwtString = localStorage.getItem(JWT_STORAGE_KEY);
    if (jwtString === null) {
      return undefined;
    }
    try {
      const credentials: AuthCredentials = JSON.parse(jwtString);
      if (credentials?.token) {
        return credentials;
      }
    } catch {
      // korrupter Eintrag - wird unten entfernt
    }
    localStorage.removeItem(JWT_STORAGE_KEY);
    return undefined;
  }

  // Der ErrorInterceptor meldet jeden HTTP-Fehler dieses Requests bereits selbst.
  public async authenticate(username: string, password: string): Promise<AuthCredentials | null> {
    let result: AuthenticationResultDto;
    try {
      result = await firstValueFrom(this.http.post<AuthenticationResultDto>(
        this.apiConfig.basePath + '/api/auth/login',
        {
          'userName': username,
          password,
        },
      ).pipe(timeout(30000)));
    } catch (error) {
      console.error(error);
      if (error instanceof TimeoutError) {
        this.nbToastrService.danger('Der Server antwortet nicht. Bitte versuchen Sie es erneut.', 'Fehler');
      }
      return null;
    }

    if (result.error) {
      console.error(result.error);
      this.nbToastrService.danger(result.error.message, 'Fehler');
      return null;
    }
    if (!result.result) {
      this.nbToastrService.danger('Unerwartete Antwort vom Server. Bitte erneut versuchen.', 'Fehler');
      return null;
    }

    const credentials = new AuthCredentials(
      result.result.id,
      result.result.userName,
      result.result.token,
      result.result.mustChangePassword ?? false,
    );

    // setItem kann werfen (Safari Private Mode, blockierte Cookies, QuotaExceededError).
    // Die Login-Seite meldet nur den Erfolgsfall - ohne diesen Zweig waere das ein
    // Login-Klick ohne jede Rueckmeldung.
    try {
      localStorage.setItem(JWT_STORAGE_KEY, JSON.stringify(credentials));
    } catch (error) {
      console.error(error);
      this.nbToastrService.danger('Anmeldedaten konnten nicht gespeichert werden.', 'Fehler');
      return null;
    }
    Sentry.setUser({
      id: credentials.id,
      username: credentials.username,
    });
    this.authCredentials.set(credentials);
    return credentials;
  }

  // Das Flag im localStorage stammt vom Login und veraltet, sobald ein Admin das Passwort
  // zurücksetzt. Ohne diese Korrektur schickt der AuthGuard den Nutzer von der
  // Force-Change-Seite sofort wieder weg, weil er ihn für nicht geflaggt hält.
  public markPasswordChangeRequired(): void {
    const credentials = this.authCredentials();
    if (!credentials || credentials.mustChangePassword) return;
    const updated = new AuthCredentials(credentials.id, credentials.username, credentials.token, true);
    localStorage.setItem(JWT_STORAGE_KEY, JSON.stringify(updated));
    this.authCredentials.set(updated);
  }

  // Der Passwortwechsel widerruft alle Tokens des Benutzers (WR-2385) und gibt der auslösenden
  // Sitzung ein frisches zurück. Ohne Übernahme liefe dieser Browser mit dem widerrufenen Token
  // weiter und fiele beim nächsten Request in den 401-Abmeldepfad. Das Flag ist danach erledigt.
  public adoptRotatedToken(token: string): void {
    const credentials = this.authCredentials();
    if (!credentials) return;
    const updated = new AuthCredentials(credentials.id, credentials.username, token, false);
    // Schlägt der Storage fehl (Safari Private Mode), lebt die Sitzung wenigstens im Tab weiter -
    // das Passwort ist serverseitig ohnehin schon geändert.
    try {
      localStorage.setItem(JWT_STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error(error);
    }
    this.authCredentials.set(updated);
  }

  public async logout({ revokeServerSession = true }: LogoutOptions = {}): Promise<void> {
    if (revokeServerSession) {
      this.revokeServerSession();
    }
    this.discardCredentials();
    await this.router.navigate(['/login']);
  }

  public discardCredentials(): void {
    localStorage.removeItem(JWT_STORAGE_KEY);
    this.authCredentials.set(undefined);
    Sentry.setUser(null);
  }

  // Muss VOR dem Leeren der Credentials abgesetzt werden - der AuthInterceptor liest den
  // Token beim Absenden, ein Reihenfolgentausch schickt den Request unauthentifiziert los
  // und der Server widerruft nichts. Auf die Antwort wird bewusst nicht gewartet.
  private revokeServerSession(): void {
    if (!this.authCredentials()) return;
    this.authApi.signOut().subscribe({ error: () => undefined });
  }
}
